/**
 * Modül Kontrolü — şirket bazlı modül aç/kapa tanımları.
 *
 * SuperAdmin bir şirketin kullanabileceği modülleri kısıtlayabilir
 * (Companies.EnabledModulesJson). Bu dosya tek doğruluk kaynağıdır:
 *  - Modül id'leri backend SuperAdminController.ValidModuleIds ve Sidebar
 *    bölüm id'leri ile birebir senkrondur.
 *  - Rota → modül eşlemesi hem MainLayout rota korumasında hem menü gate'lerinde kullanılır.
 *
 * Kurallar:
 *  - enabledModules === null → varsayılan modüller açık. Opt-in modüller (üretim) bu kapsama girmez;
 *    yalnız listede açıkça varsa açıktır.
 *  - Bir path hiçbir modüle eşlenmemişse (profil, hızlı başlangıç vb.) her zaman erişilebilir.
 *  - Bir path birden çok modüle eşlenebilir (ör. resim kütüphanesi) — herhangi biri açıksa erişilir.
 */

export type ModuleId =
  | 'task_tracking'
  | 'accounting'
  | 'sales_system'
  | 'hr'
  | 'wms'
  | 'device_management'
  | 'card_management'
  | 'branches'
  | 'other'
  | 'production'
  | 'production_ops'
  | 'lot_tracking';

export interface ModuleDefinition {
  id: ModuleId;
  labelTr: string;
  labelEn: string;
  descriptionTr: string;
}

export const MODULE_DEFINITIONS: ModuleDefinition[] = [
  { id: 'task_tracking', labelTr: 'Görev Takip', labelEn: 'Task Tracking', descriptionTr: 'Dashboard, görevler, raporlar, mesajlar, anketler' },
  { id: 'accounting', labelTr: 'Ön Muhasebe', labelEn: 'Pre-Accounting', descriptionTr: 'Cari, stok, banka, kasa, fatura, teklif, sipariş, irsaliye, çek/senet' },
  { id: 'sales_system', labelTr: 'Satış Sistemleri', labelEn: 'Sales Systems', descriptionTr: 'Hızlı satış, restoran, QR menü, kiosk, e-ticaret, kurye, entegrasyonlar' },
  { id: 'hr', labelTr: 'İnsan Kaynakları', labelEn: 'Human Resources', descriptionTr: 'Personel, vardiyalar, işe alım, CV başvuruları' },
  { id: 'wms', labelTr: 'Akıllı Depo (WMS)', labelEn: 'Smart Warehouse (WMS)', descriptionTr: 'Depo tanımlama, sipariş havuzu, iş emirleri, sayım' },
  { id: 'device_management', labelTr: 'Cihaz Yönetimi', labelEn: 'Device Management', descriptionTr: 'Bağlı cihazların yönetimi' },
  { id: 'card_management', labelTr: 'Kart & Cüzdan', labelEn: 'Card & Wallet', descriptionTr: 'Kart yönetimi ve oturumları' },
  { id: 'branches', labelTr: 'Şubeler', labelEn: 'Branches', descriptionTr: 'Şube yönetimi' },
  { id: 'other', labelTr: 'Diğer', labelEn: 'Other', descriptionTr: 'Rezervasyonlar, etkinlikler, mail raporları' },
  { id: 'production', labelTr: 'Üretim', labelEn: 'Production', descriptionTr: 'Üretim fişleri, yarı mamul stoklama, ürün ağacı, üretim raporları (ayrıca açılır)' },
  { id: 'production_ops', labelTr: 'Üretim - Operasyonlar', labelEn: 'Production - Operations', descriptionTr: 'İş istasyonları, rota, operasyonlu üretim emri ve atölye ekranı (Üretim modülü gerekir)' },
  { id: 'lot_tracking', labelTr: 'Lot / Parti Takibi', labelEn: 'Lot Tracking', descriptionTr: 'Lot numarası, son kullanma tarihi, FEFO çıkış ve geriye dönük izlenebilirlik' },
];

export const ALL_MODULE_IDS: ModuleId[] = MODULE_DEFINITIONS.map((m) => m.id);

/** "Tümü açık" (enabledModules === null) kapsamına girmeyen, yalnız açıkça seçilince açılan modüller. */
export const OPT_IN_MODULE_IDS: ModuleId[] = ['production', 'production_ops', 'lot_tracking'];

/** production_ops, üretim kapalıyken açık sayılmaz. */
export const MODULE_REQUIRES: Partial<Record<ModuleId, ModuleId[]>> = {
  production_ops: ['production'],
};

/**
 * GEÇİCİ ÖN İZLEME: Üretim modülü, süper admin modül paneline bağlanana kadar
 * her şirkette açık sayılır. Modül listesi ana DB'de (Companies.EnabledModulesJson)
 * tutulduğu ve API'ler henüz güncellenmediği için modül oradan açılamıyor.
 *
 * Süper admin paneline bağlandığında bu sabit `false` yapılır; başka hiçbir yer
 * değişmez — `isModuleEnabled` normal opt-in kuralına döner.
 */
export const PRODUCTION_PREVIEW_ALWAYS_ON = false;
/** null ("tümü açık") somutlaştırılırken kullanılacak liste. */
export const DEFAULT_ON_MODULE_IDS: ModuleId[] = ALL_MODULE_IDS.filter((m) => !OPT_IN_MODULE_IDS.includes(m));

/**
 * Rota öneki → modül eşlemesi. Önek eşleşmesi segment duyarlıdır:
 * '/siparis' → '/siparis/satis/yeni' eşleşir ama '/siparis-yonetimi' EŞLEŞMEZ.
 * Daha uzun (spesifik) önekler önce kontrol edilir.
 */
const ROUTE_MODULE_MAP: { prefix: string; modules: ModuleId[] }[] = [
  // --- Spesifik istisnalar (önce) ---
  { prefix: '/accounting/mail-raporlari', modules: ['other'] },

  // --- Görev Takip ---
  { prefix: '/dashboard', modules: ['task_tracking'] },
  { prefix: '/tasks', modules: ['task_tracking'] },
  { prefix: '/reports', modules: ['task_tracking'] },
  { prefix: '/messages', modules: ['task_tracking'] },
  { prefix: '/surveys', modules: ['task_tracking'] },

  // --- İnsan Kaynakları ---
  { prefix: '/personnel', modules: ['hr'] },
  { prefix: '/shifts', modules: ['hr'] },
  { prefix: '/ik', modules: ['hr'] },
  { prefix: '/hr', modules: ['hr'] },

  // --- Ön Muhasebe ---
  { prefix: '/accounting', modules: ['accounting'] },
  { prefix: '/cari', modules: ['accounting'] },
  { prefix: '/cari-hareket-raporu', modules: ['accounting'] },
  { prefix: '/stok/lotlar', modules: ['lot_tracking'] },
  { prefix: '/stok', modules: ['accounting'] },
  { prefix: '/stok-hareket-raporu', modules: ['accounting'] },
  { prefix: '/fiyat-degisim-loglari', modules: ['accounting'] },
  { prefix: '/basit-uretim-receteleri', modules: ['accounting'] },
  { prefix: '/urun-menuleri', modules: ['accounting'] },
  { prefix: '/sube-fiyatlari', modules: ['accounting'] },
  { prefix: '/etiket-yazdirma', modules: ['accounting'] },
  { prefix: '/banka', modules: ['accounting'] },
  { prefix: '/bank', modules: ['accounting'] },
  { prefix: '/kredi-kartlari', modules: ['accounting'] },
  { prefix: '/krediler', modules: ['accounting'] },
  { prefix: '/kredi-odemeleri', modules: ['accounting'] },
  { prefix: '/kasa', modules: ['accounting'] },
  { prefix: '/depo', modules: ['accounting'] },
  { prefix: '/doviz', modules: ['accounting'] },
  { prefix: '/currency', modules: ['accounting'] },
  { prefix: '/fatura', modules: ['accounting'] },
  { prefix: '/e-fatura', modules: ['accounting'] },
  { prefix: '/teklifler', modules: ['accounting'] },
  { prefix: '/irsaliyeler', modules: ['accounting'] },
  { prefix: '/irsaliye', modules: ['accounting'] },
  { prefix: '/siparisler', modules: ['accounting'] },
  { prefix: '/siparis', modules: ['accounting'] },
  { prefix: '/firmalar-arasi-b2b', modules: ['accounting'] },
  { prefix: '/cek-senet', modules: ['accounting'] },
  { prefix: '/kar-zarar', modules: ['accounting'] },
  { prefix: '/gider-turleri', modules: ['accounting'] },

  // --- Üretim (uzun önek önce) ---
  { prefix: '/uretim/atolye', modules: ['production_ops'] },
  { prefix: '/uretim/tanimlar', modules: ['production_ops'] },
  { prefix: '/uretim', modules: ['production'] },

  // --- Satış Sistemleri ---
  { prefix: '/hizli-satis', modules: ['sales_system'] },
  { prefix: '/restoran', modules: ['sales_system'] },
  { prefix: '/restaurant-orders', modules: ['sales_system'] },
  { prefix: '/restaurant-screen', modules: ['sales_system'] },
  { prefix: '/siparis-yonetimi', modules: ['sales_system'] },
  { prefix: '/tum-siparisler', modules: ['sales_system'] },
  { prefix: '/paket-ekrani', modules: ['sales_system'] },
  { prefix: '/gun-sonu', modules: ['sales_system'] },
  { prefix: '/gun-sonlari', modules: ['sales_system'] },
  { prefix: '/qr-menu-management', modules: ['sales_system'] },
  { prefix: '/qr-menu', modules: ['sales_system'] },
  { prefix: '/mutfak-ekrani-secimi', modules: ['sales_system'] },
  { prefix: '/mutfak-ekrani', modules: ['sales_system'] },
  { prefix: '/musteri-ekrani-yonetimi', modules: ['sales_system'] },
  { prefix: '/musteri-ekrani', modules: ['sales_system'] },
  { prefix: '/customer-screen', modules: ['sales_system'] },
  { prefix: '/kiosk-menu', modules: ['sales_system'] },
  { prefix: '/kiosk-products', modules: ['sales_system'] },
  { prefix: '/kulup-giris', modules: ['sales_system'] },
  { prefix: '/e-commerce', modules: ['sales_system'] },
  { prefix: '/kurye', modules: ['sales_system'] },
  { prefix: '/entegrasyonlar', modules: ['sales_system'] },
  { prefix: '/hacioglu-urun-eslestirme', modules: ['sales_system'] },

  // --- Paylaşılan (herhangi biri açıksa erişilir) ---
  { prefix: '/resim-kutuphanesi', modules: ['accounting', 'sales_system'] },

  // --- WMS ---
  { prefix: '/wms', modules: ['wms'] },

  // --- Cihaz / Kart / Şube / Diğer ---
  { prefix: '/cihaz-yonetimi', modules: ['device_management'] },
  { prefix: '/kart-yonetimi', modules: ['card_management'] },
  { prefix: '/branches', modules: ['branches'] },
  { prefix: '/events', modules: ['other'] },
  { prefix: '/reservations', modules: ['other'] },
];

/** Segment duyarlı önek eşleşmesi: '/siparis' ile '/siparis-yonetimi' eşleşmez. */
const pathMatchesPrefix = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

/** Path'in ait olduğu modüller (eşleşme yoksa boş dizi = serbest sayfa). */
export const getModulesForPath = (pathname: string): ModuleId[] => {
  // En uzun (en spesifik) önek kazanır.
  let best: { prefix: string; modules: ModuleId[] } | null = null;
  for (const entry of ROUTE_MODULE_MAP) {
    if (pathMatchesPrefix(pathname, entry.prefix)) {
      if (!best || entry.prefix.length > best.prefix.length) best = entry;
    }
  }
  return best ? best.modules : [];
};

/** Modül açık mı? null iken varsayılan modüller açıktır; opt-in modüller kapalıdır. Üst modül kapalıysa alt modül de kapalıdır. */
export const isModuleEnabled = (
  moduleId: ModuleId | string,
  enabledModules: string[] | null | undefined
): boolean => {
  if (PRODUCTION_PREVIEW_ALWAYS_ON && moduleId === 'production') return true;
  const self = enabledModules == null
    ? !OPT_IN_MODULE_IDS.includes(moduleId as ModuleId)
    : enabledModules.some((id) => id.toLowerCase() === String(moduleId).toLowerCase());
  if (!self) return false;
  const parents = MODULE_REQUIRES[moduleId as ModuleId];
  if (!parents || parents.length === 0) return true;
  return parents.every((parent) => isModuleEnabled(parent, enabledModules));
};

/** Path'e erişilebilir mi? Eşlenmemiş path'ler her zaman serbesttir. */
export const isPathAllowed = (
  pathname: string,
  enabledModules: string[] | null | undefined
): boolean => {
  const modules = getModulesForPath(pathname);
  if (modules.length === 0) return true;
  return modules.some((m) => isModuleEnabled(m, enabledModules));
};
